/**
 * OAuth 2.1 認可コード + PKCE のクライアント側。
 *
 * 保持の方針:
 * - アクセストークン / リフレッシュトークンは**メモリだけ**に置く（token-store.ts）。
 *   タブを閉じたりリロードしたら消える。消えたら認可からやり直す。
 * - 認可リダイレクトをまたぐ必要がある verifier / state / 戻り先パスだけは
 *   sessionStorage に置く。これらはトークンではなく、1 往復で使い切る値で、
 *   使った時点（成功・失敗どちらでも）で消す。
 */

import {
  CODE_CHALLENGE_METHOD,
  createCodeVerifier,
  createState,
  deriveCodeChallenge,
} from "./pkce";

export type AuthorizationServerEndpoints = {
  authorizationEndpoint: string;
  tokenEndpoint: string;
  /** 動的クライアント登録（RFC 7591）。メタデータに無ければ null */
  registrationEndpoint: string | null;
};

export type TokenSet = {
  accessToken: string;
  tokenType: string;
  /** epoch ミリ秒。expires_in が無ければ null（失効は 401 で気づく） */
  expiresAt: number | null;
  refreshToken: string | null;
};

export type PendingAuthorization = {
  state: string;
  codeVerifier: string;
  redirectUri: string;
  /** 認可前に見ていたパス。コールバック後にここへ戻す */
  returnTo: string;
  /** 認可し直しでテナントを指定した場合、その ID */
  tenantId: string | null;
  /** セッション復元の 1 回限りの往復。 */
  silent?: boolean;
  expectedDelegationId?: string | null;
};

export class OAuthError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = "OAuthError";
  }
}

const PENDING_KEY = "visumo.admin.oauth.pending";

/** sessionStorage は private モード等で例外を投げうる。認証全体を落とさない */
function safeSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function savePendingAuthorization(pending: PendingAuthorization): void {
  safeSessionStorage()?.setItem(PENDING_KEY, JSON.stringify(pending));
}

/** 読み出しと同時に消す。1 往復で使い切る値なので残さない */
export function takePendingAuthorization(): PendingAuthorization | null {
  const storage = safeSessionStorage();
  const raw = storage?.getItem(PENDING_KEY) ?? null;
  storage?.removeItem(PENDING_KEY);
  if (raw === null) {
    return null;
  }
  try {
    return JSON.parse(raw) as PendingAuthorization;
  } catch {
    return null;
  }
}

export function clearPendingAuthorization(): void {
  safeSessionStorage()?.removeItem(PENDING_KEY);
}

/**
 * RFC 8414 のメタデータから認可・トークンのエンドポイントを引く。
 * issuer がパス付きのときは、パス差し込み形（`/.well-known/...<path>`）と
 * 接尾形（`<issuer>/.well-known/...`）の両方を試す。サーバーの流儀に寄せない。
 */
export async function discoverEndpoints(
  issuer: string,
  fetchImpl: typeof fetch = fetch,
): Promise<AuthorizationServerEndpoints> {
  const url = new URL(issuer);
  const path = url.pathname === "/" ? "" : url.pathname;
  const candidates = [
    `${url.origin}/.well-known/oauth-authorization-server${path}`,
    `${issuer}/.well-known/oauth-authorization-server`,
  ];
  const failures: string[] = [];
  for (const candidate of candidates) {
    let document: unknown;
    try {
      const response = await fetchImpl(candidate, {
        headers: { accept: "application/json" },
      });
      if (!response.ok) {
        failures.push(`${candidate}: HTTP ${response.status}`);
        continue;
      }
      document = await response.json();
    } catch (error) {
      failures.push(`${candidate}: ${String(error)}`);
      continue;
    }
    const metadata = document as Record<string, unknown>;
    const authorizationEndpoint = metadata["authorization_endpoint"];
    const tokenEndpoint = metadata["token_endpoint"];
    const registrationEndpoint = metadata["registration_endpoint"];
    if (
      typeof authorizationEndpoint === "string" &&
      typeof tokenEndpoint === "string"
    ) {
      return {
        authorizationEndpoint,
        tokenEndpoint,
        registrationEndpoint:
          typeof registrationEndpoint === "string"
            ? registrationEndpoint
            : null,
      };
    }
    failures.push(`${candidate}: エンドポイントが欠けている`);
  }
  throw new OAuthError(
    `認可サーバーのメタデータを取得できません（${failures.join(" / ")}）`,
    "discovery_failed",
  );
}

export type AuthorizationRequest = {
  url: string;
  pending: PendingAuthorization;
};

export async function createAuthorizationRequest(input: {
  endpoints: AuthorizationServerEndpoints;
  clientId: string;
  scope: string;
  redirectUri: string;
  returnTo: string;
  tenantId?: string | null;
  tenantParam?: string;
  /** access token の aud を指定する（RFC 8707）。未指定なら送らない */
  resource?: string | null;
  /** トップレベルの遷移で黙って取り直すとき。認可画面を出させない */
  prompt?: "none";
}): Promise<AuthorizationRequest> {
  const codeVerifier = createCodeVerifier();
  const state = createState();
  const challenge = await deriveCodeChallenge(codeVerifier);
  const url = new URL(input.endpoints.authorizationEndpoint);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", input.clientId);
  url.searchParams.set("redirect_uri", input.redirectUri);
  url.searchParams.set("scope", input.scope);
  url.searchParams.set("state", state);
  url.searchParams.set("code_challenge", challenge);
  url.searchParams.set("code_challenge_method", CODE_CHALLENGE_METHOD);
  if (input.resource != null && input.resource !== "") {
    url.searchParams.set("resource", input.resource);
  }
  if (input.prompt !== undefined) {
    url.searchParams.set("prompt", input.prompt);
  }
  const tenantId = input.tenantId ?? null;
  if (tenantId !== null && input.tenantParam !== undefined) {
    url.searchParams.set(input.tenantParam, tenantId);
  }
  return {
    url: url.toString(),
    pending: {
      state,
      codeVerifier,
      redirectUri: input.redirectUri,
      returnTo: input.returnTo,
      tenantId,
      silent: input.prompt === "none",
    },
  };
}

type TokenResponse = {
  access_token?: unknown;
  token_type?: unknown;
  expires_in?: unknown;
  refresh_token?: unknown;
  error?: unknown;
  error_description?: unknown;
};

function toTokenSet(payload: TokenResponse, now: number): TokenSet {
  if (typeof payload.access_token !== "string") {
    throw new OAuthError(
      "トークン応答に access_token がありません",
      "invalid_token_response",
    );
  }
  const expiresIn = payload.expires_in;
  return {
    accessToken: payload.access_token,
    tokenType:
      typeof payload.token_type === "string" ? payload.token_type : "Bearer",
    expiresAt: typeof expiresIn === "number" ? now + expiresIn * 1000 : null,
    refreshToken:
      typeof payload.refresh_token === "string" ? payload.refresh_token : null,
  };
}

async function postToken(
  tokenEndpoint: string,
  body: URLSearchParams,
  fetchImpl: typeof fetch,
  now: number,
): Promise<TokenSet> {
  const response = await fetchImpl(tokenEndpoint, {
    method: "POST",
    headers: {
      "content-type": "application/x-www-form-urlencoded",
      accept: "application/json",
    },
    body: body.toString(),
  });
  let payload: TokenResponse = {};
  try {
    payload = (await response.json()) as TokenResponse;
  } catch {
    payload = {};
  }
  if (!response.ok) {
    const code =
      typeof payload.error === "string"
        ? payload.error
        : `http_${response.status}`;
    const description =
      typeof payload.error_description === "string"
        ? payload.error_description
        : `トークンエンドポイントが ${response.status} を返しました`;
    throw new OAuthError(description, code);
  }
  return toTokenSet(payload, now);
}

export async function exchangeAuthorizationCode(input: {
  tokenEndpoint: string;
  clientId: string;
  code: string;
  codeVerifier: string;
  redirectUri: string;
  resource?: string | null;
  fetchImpl?: typeof fetch;
  now?: number;
}): Promise<TokenSet> {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code: input.code,
    client_id: input.clientId,
    redirect_uri: input.redirectUri,
    code_verifier: input.codeVerifier,
  });
  if (input.resource != null && input.resource !== "") {
    body.set("resource", input.resource);
  }
  return postToken(
    input.tokenEndpoint,
    body,
    input.fetchImpl ?? fetch,
    input.now ?? Date.now(),
  );
}

export async function refreshAccessToken(input: {
  tokenEndpoint: string;
  clientId: string;
  refreshToken: string;
  resource?: string | null;
  fetchImpl?: typeof fetch;
  now?: number;
}): Promise<TokenSet> {
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: input.refreshToken,
    client_id: input.clientId,
  });
  if (input.resource != null && input.resource !== "") {
    body.set("resource", input.resource);
  }
  return postToken(
    input.tokenEndpoint,
    body,
    input.fetchImpl ?? fetch,
    input.now ?? Date.now(),
  );
}

/** 認可サーバーからの戻りを読む。エラー応答（`error=...`）もここで型にする */
export type CallbackParams =
  | { kind: "code"; code: string; state: string }
  | { kind: "error"; error: string; description: string | null }
  | { kind: "none" };

export function readCallbackParams(search: string): CallbackParams {
  const params = new URLSearchParams(search);
  const error = params.get("error");
  if (error !== null) {
    return {
      kind: "error",
      error,
      description: params.get("error_description"),
    };
  }
  const code = params.get("code");
  const state = params.get("state");
  if (code !== null && state !== null) {
    return { kind: "code", code, state };
  }
  return { kind: "none" };
}
